// Phase 7 R2 — procurement panel injected into TicketPartsPanel.
// Lists parts lines on this ticket eligible to be added to a new PO.
//
// CANONICAL eligibility (R2): parts_line.procurement_status === 'not_ordered'
// AND not soft-deleted. This replaces the R1 reverse-join proxy against
// purchase_order_lines — single source of truth, no transitional dual-logic.
//
// PRESENTATION RULE (see ProcurementStatusPill): the pill is hidden when
// procurement_status === 'not_ordered'. That is purely visual. This filter
// reads procurement_status directly from the row and NEVER from pill state.
import { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { ShoppingCart } from 'lucide-react';
import { useUserRole } from '@/hooks/useUserRole';
import { CreatePurchaseOrderDialog } from './CreatePurchaseOrderDialog';
import type { PartsLine } from '@/hooks/parts/usePartsLines';

interface Props {
  ticketId: string;
  shopId: string;
  lines: PartsLine[];
}

export function TicketProcurementPanel({ ticketId, shopId, lines }: Props) {
  const { role } = useUserRole();
  const canCreatePO = role === 'OWNER' || role === 'MANAGER' || role === 'SERVICE_WRITER';

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [dialogOpen, setDialogOpen] = useState(false);

  // R2 canonical eligibility — reads procurement_status directly.
  const eligibleLines = useMemo(
    () => lines.filter((l) => l.procurement_status === 'not_ordered' && !l.deleted_at),
    [lines],
  );

  if (!canCreatePO) return null;
  if (eligibleLines.length === 0) return null;

  const toggle = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selectedIds.size === eligibleLines.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(eligibleLines.map((l) => l.id)));
  };

  const selectedLines = eligibleLines.filter((l) => selectedIds.has(l.id));

  return (
    <div className="rounded-lg border bg-muted/10 p-3 space-y-3">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-sm font-medium flex items-center gap-2">
            <ShoppingCart className="h-4 w-4" /> Procurement
          </div>
          <div className="text-xs text-muted-foreground">
            {eligibleLines.length} part{eligibleLines.length === 1 ? '' : 's'} not yet on a PO.
            {selectedIds.size > 0 && ` ${selectedIds.size} selected.`}
          </div>
        </div>
        <Button
          size="sm"
          disabled={selectedIds.size === 0}
          onClick={() => setDialogOpen(true)}
        >
          Create PO ({selectedIds.size})
        </Button>
      </div>

      <div className="space-y-1">
        <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
          <Checkbox
            checked={selectedIds.size === eligibleLines.length && eligibleLines.length > 0}
            onCheckedChange={toggleAll}
          />
          Select all eligible
        </label>
        {eligibleLines.map((l) => (
          <label
            key={l.id}
            className="flex items-center gap-2 rounded-md border bg-card px-2 py-1.5 text-sm cursor-pointer hover:bg-accent/30"
          >
            <Checkbox
              checked={selectedIds.has(l.id)}
              onCheckedChange={() => toggle(l.id)}
            />
            <div className="flex-1 min-w-0">
              <div className="truncate font-medium">{l.description}</div>
              <div className="text-xs text-muted-foreground">
                Qty {Number(l.quantity)}
                {l.vendor_name && ` · ${l.vendor_name}`}
                {l.vendor_part_number && ` · ${l.vendor_part_number}`}
              </div>
            </div>
            {l.unit_cost != null && (
              <div className="text-xs tabular-nums text-muted-foreground">
                @ {Number(l.unit_cost).toLocaleString(undefined, { style: 'currency', currency: 'USD' })}
              </div>
            )}
          </label>
        ))}
      </div>

      <CreatePurchaseOrderDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        ticketId={ticketId}
        selectedLines={selectedLines}
        onSuccess={() => setSelectedIds(new Set())}
      />
    </div>
  );
}
